import React from "react";

import DebugCodeBlock from "@components/DebugCodeBlock";

import ExpandedTextAd from "../ExpandedTextAd";
import ImageAd from "../ImageAd";
import PrettyDisplayAttributes from "../PrettyDisplayAttributes";
import TextAd from "../TextAd";

import GmailAd from "./GmailAd";
import ResponsiveDisplayAd from "./ResponsiveDisplayAd";
import ResponsiveSearchAd from "./ResponsiveSearchAd";

function getAdAndNbMetada(ad: any) {
  let nbMetadata: any = null;
  if (ad.googleMetadata && ad.nbMetadata) {
    nbMetadata = ad.nbMetadata;
    ad = ad.googleMetadata;
  }

  ad = ad.ad;
  return { ad, nbMetadata };
}

export default function GoogleAd({ googleAd }: { googleAd: any }) {
  const { ad, nbMetadata } = getAdAndNbMetada(googleAd);

  const ytID = nbMetadata?.youtube_video_id;
  if (ytID) {
    return (
      <div>
        <iframe
          title={`YouTube video: ${ytID}`}
          width="560"
          height="315"
          src={`https://www.youtube.com/embed/${ytID}`}
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
    );
  } else if (ad.type.startsWith("VIDEO_")) {
    return (
      <div className="text-black text-center flex-1">
        YouTube Ad <br />
        (no preview available)
      </div>
    );
  }

  if (ad.type === "TEXT_AD") {
    return <TextAd {...ad.textAd} displayUrl={ad.displayUrl} />;
  } else if (ad.type === "EXPANDED_TEXT_AD") {
    return <ExpandedTextAd {...ad.expandedTextAd} />;
  } else if (ad.type === "IMAGE_AD") {
    return <ImageAd {...ad.imageAd} />;
  } else if (
    ad.type === "PRODUCT_AD" ||
    ad.type === "GOAL_OPTIMIZED_SHOPPING_AD"
  ) {
    return (
      <PrettyDisplayAttributes
        attributes={[
          { key: "Type", value: ad["Ad.Type"] },
          { key: "ID", value: ad.id },
        ]}
      />
    );
  } else if (ad.type === "TEMPLATE_AD") {
    return (
      <PrettyDisplayAttributes
        attributes={[
          { key: "Type", value: ad["Ad.Type"] },
          { key: "Name", value: ad.name },
          { key: "Display URL", value: ad.displayUrl },
          { key: "Final URL", value: JSON.stringify(ad.finalUrls) },
          { key: "ID", value: ad.id },
        ]}
      />
    );
  } else if (ad.type === "DYNAMIC_SEARCH_AD") {
    return (
      <TextAd
        {...ad}
        headline="[Dynamically generated headline]"
        description={ad.description1}
      />
    );
  } else if (ad.type === "EXPANDED_DYNAMIC_SEARCH_AD") {
    return (
      <TextAd
        {...ad}
        headline="[Dynamically generated headline]"
        displayUrl="[Dynamically generated display URL]"
        description={ad.description1}
      />
    );
  } else if (ad.type === "GMAIL_AD") {
    return <GmailAd {...ad.gmailAd} />;
  } else if (ad.type === "RESPONSIVE_DISPLAY_AD") {
    return <ResponsiveDisplayAd {...ad.responsiveDisplayAd} />;
  } else if (ad.type === "RESPONSIVE_SEARCH_AD") {
    return <ResponsiveSearchAd {...ad} />;
  }
  return <DebugCodeBlock data={ad} style={{ minWidth: "480px" }} />;
}

type GoogleAdTypes =
  | "RESPONSIVE_DISPLAY_AD"
  | "IMAGE_AD"
  | "VIDEO_TRUEVIEW_IN_STREAM_AD"
  | "IN_FEED_VIDEO_AD"
  | "VIDEO_BUMPER_AD"
  | "VIDEO_RESPONSIVE_AD"
  | "DISCOVERY_MULTI_ASSET_AD"
  | "DISCOVERY_CAROUSEL_AD"
  | "LEGACY_RESPONSIVE_DISPLAY_AD"
  | "VIDEO_NON_SKIPPABLE_IN_STREAM_AD";

type BaseGoogleAdAsset = {
  headline: string;
  description: string;
};

type VideoAsset = BaseGoogleAdAsset & {
  video_id: string;
};
type ImageAsset = BaseGoogleAdAsset & {
  imageUrl: string;
};
export type GoogleAdData = {
  adType: GoogleAdTypes;

  assetsArray: Array<ImageAsset | VideoAsset>;
};
export function GoogleAdV2({ adType, assetsArray }: GoogleAdData) {
  if (!assetsArray?.length) {
    return <div className="text-center mt-5">No preview available.</div>;
  }
  // all ads should be just image or video unless they are "RESPONSIVE_DISPLAY_AD" ads
  if (adType === "RESPONSIVE_DISPLAY_AD") {
    return (
      <div>
        {assetsArray.map((asset) => {
          if (isVideo(asset)) {
            return (
              <div key={asset.video_id}>
                <Video asset={asset} />
                <div>
                  <div>{asset.headline}</div>
                  <div>{asset.description}</div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={asset.imageUrl}
              className="border border-v2-secondary rounded-xl overflow-hidden"
            >
              <Image asset={asset} />
              <div className="p-2">
                <div className="text-md font-semibold">{asset.headline}</div>
                <div>{asset.description}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {assetsArray.map((asset) => {
        if (isVideo(asset)) {
          return (
            <div key={asset.video_id}>
              <Video asset={asset} />
            </div>
          );
        }

        return <Image asset={asset} key={asset.imageUrl} />;
      })}
    </div>
  );
}

function isVideo(asset: any): asset is VideoAsset {
  return typeof asset.video_id === "string";
}

function Image({ asset }: { asset: ImageAsset }) {
  return <img src={asset.imageUrl} alt="Google ad preview" />;
}

function Video({ asset }: { asset: VideoAsset }) {
  return (
    <iframe
      title={`YouTube video: ${asset.video_id}`}
      width="426"
      height="240"
      src={`https://www.youtube.com/embed/${asset.video_id}`}
      frameBorder="0"
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    ></iframe>
  );
}
